/**
 * SubjectFormModal — zakładanie i zmiana nazwy roli albo zespołu (F24 Kawałek 1).
 *
 * Jedno okienko na cztery przypadki (rola/zespół × nowy/edycja), bo formularz jest w każdym z
 * nich ten sam: nazwa i opis. Uprawnienia nadaje się w drzewie obok, więc nie ma tu ani listy
 * kodów ze starego modelu, ani wyboru firmy — firma wynika z kontekstu ekranu.
 *
 * Zastąpiło osobne ekrany „Szablony ról" i „Zespoły": trzymanie zakładania roli w jednym miejscu,
 * a nadawania jej praw w drugim zmuszało do skakania tam i z powrotem przy każdej nowej roli.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { TEAMS_KEY } from '@/lib/use-permission-center';

export interface SubjectFormTarget {
  kind: 'role' | 'team';
  /** `null` = zakładamy nowy podmiot. */
  id: string | null;
  name: string;
  description: string | null;
}

interface SubjectFormModalProps {
  open: boolean;
  target: SubjectFormTarget | null;
  /** Firma, w której powstaje rola albo zespół — oba należą do jednej firmy. */
  companyId: string | null;
  onClose: () => void;
  /** Po założeniu nowego podmiotu ekran przestawia się na niego. */
  onCreated: (kind: 'role' | 'team', id: string) => void;
}

export function SubjectFormModal({
  open,
  target,
  companyId,
  onClose,
  onCreated,
}: SubjectFormModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !target) return;
    setName(target.name);
    setDescription(target.description ?? '');
    setError(null);
  }, [open, target]);

  const isNew = target?.id == null;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
      };
      if (target!.kind === 'role') {
        if (isNew) {
          const res = await api.post<{ roleTemplate: { id: string } }>('/role-templates', {
            companyId,
            name: body.name,
            description: body.description ?? undefined,
          });
          return res.data.roleTemplate.id;
        }
        await api.patch(`/role-templates/${target!.id}`, body);
        return target!.id!;
      }
      if (isNew) {
        const res = await api.post<{ team: { id: string } }>('/teams', {
          companyId,
          name: body.name,
          description: body.description ?? undefined,
        });
        return res.data.team.id;
      }
      await api.patch(`/teams/${target!.id}`, body);
      return target!.id!;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'center', 'roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-templates'] });
      void queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      toast.show({
        variant: 'success',
        title: isNew
          ? t(`tenant.permissions.center.subjectAdmin.created.${target!.kind}`)
          : t('tenant.permissions.center.subjectAdmin.renamed'),
      });
      if (isNew) onCreated(target!.kind, id);
      onClose();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      setError(
        code === 'NAME_TAKEN'
          ? t('tenant.permissions.center.subjectAdmin.nameTaken')
          : code === 'SYSTEM_ROLE_IMMUTABLE'
            ? t('tenant.permissions.center.subjectAdmin.systemRole')
            : t('tenant.permissions.center.saveError'),
      );
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (name.trim() === '') {
      setError(t('tenant.permissions.center.subjectAdmin.nameRequired'));
      return;
    }
    setError(null);
    save.mutate();
  }

  if (!target) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        isNew
          ? `tenant.permissions.center.subjectAdmin.new.${target.kind}`
          : `tenant.permissions.center.subjectAdmin.rename.${target.kind}`,
      )}
      description={
        isNew ? t(`tenant.permissions.center.subjectAdmin.newHint.${target.kind}`) : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            {t('common.cancel')}
          </Button>
          <Button form="subject-form" type="submit" loading={save.isPending}>
            {t(isNew ? 'common.create' : 'common.save')}
          </Button>
        </>
      }
    >
      <form id="subject-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        <FormField
          label={t('tenant.permissions.center.subjectAdmin.name')}
          required
          error={error ?? undefined}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
        </FormField>
        <FormField label={t('tenant.permissions.center.subjectAdmin.description')}>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
          />
        </FormField>
      </form>
    </Modal>
  );
}
